/*
Capabilities:
- CAP-MEMBER-ONBOARDING

Responsibilities:
- Render existing-member link and membership-card recovery flows for the
  Telegram web app.

Context:
- project-intelligence/capabilities/requested/member/member-onboarding.md
*/

import React, { useState, useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { useNavigate } from "react-router-dom";

import { useSimpleQuery } from '../../hooks';

import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Accordion from 'react-bootstrap/Accordion';

import { HorizontallyCenteringContainer, VerticalSeparator, tryRecoverMembershipCardID, tryLinkMember } from './utils';
import { FullWidthSpinner } from '../loader-components';
import { DEFAULT_BACKGROUND_COLOR } from './utils';


export function TelegramAppLink() {
    /*
    CAP-MEMBER-ONBOARDING entry point for existing-member linking and card-ID
    recovery.

    The current UI exposes verification by membership card ID plus either email
    or mobile number. Recovery uses the same email validation path and presents
    a generic success message regardless of whether the backend found a member.
    */
    const [isLinkingUp, setIsLinkingUp] = useState(false);
    const [isDoneLinkingUp, setIsDoneLinkingUp] = useState(false);
    const [linkToken, setLinkToken] = useState(null);
    const [isRecoveringMembershipID, setIsRecoveringMembershipID] = useState(false);
    const [isDoneRecoveringMembershipID, setIsDoneRecoveringMembershipID] = useState(false);

    const [accordionActiveKey, setAccordionActiveKey] = useState('verify-membership');
    const [validateWithEmail, setValidateWithEmail] = useState(false);
    const [validateWithMobile, setValidateWithMobile] = useState(false);

    const query = useSimpleQuery();
    const navigate = useNavigate();

    const tokenFromQueryParams = query.token || '';
    const tokenFromQueryParamsSplit = tokenFromQueryParams.split(':');
    const validTokenFromQueryParams = tokenFromQueryParamsSplit.length === 3;

    // Set title
    useEffect(() => {
        document.getElementsByTagName('title')[0].innerText = 'Swensen\'s Cool Rewards';
    }, []);

    // Redirect after grabbing token from URL (if available)
    useEffect(() => {
        if (validTokenFromQueryParams) {
            setTimeout(() => {
                setLinkToken(tokenFromQueryParams);
            }, 1000)
        }
    }, [validTokenFromQueryParams, tokenFromQueryParams]);

    const emailRecoveryEl = useRef(null);

    const membershipCardIDEl = useRef(null);
    const emailEl = useRef(null);
    const mobileNumberEl = useRef(null);
    // const dateOfBirthEl = useRef(null);

    const [overallErrorText, setOverallErrorText] = useState(null);

    const [membershipCardIDErrorTextLine1, setMembershipCardIDErrorTextLine1] = useState(null);
    const [membershipCardIDErrorTextLine2, setMembershipCardIDErrorTextLine2] = useState(null);
    const [emailErrorTextLine1, setEmailErrorTextLine1] = useState(null);
    const [emailErrorTextLine2, setEmailErrorTextLine2] = useState(null);
    const [mobileNumberErrorTextLine1, setMobileNumberErrorTextLine1] = useState(null);
    const [mobileNumberErrorTextLine2, setMobileNumberErrorTextLine2] = useState(null);
    // const [dateOfBirthErrorText, setDateOfBirthErrorText] = useState(null);

    const dataElements = {
        membershipCardID: { el: membershipCardIDEl, getValue: () => membershipCardIDEl?.current?.value && membershipCardIDEl?.current?.value.trim(), setError: (l1, l2) => { setMembershipCardIDErrorTextLine1(l1); setMembershipCardIDErrorTextLine2(l2) } },
        email: { el: emailEl, getValue: () => emailEl?.current?.value && emailEl?.current?.value.trim(), setError: (l1, l2) => { setEmailErrorTextLine1(l1); setEmailErrorTextLine2(l2); } },
        mobileNumber: { el: mobileNumberEl, getValue: () => mobileNumberEl?.current?.value && mobileNumberEl?.current?.value.trim(), setError: (l1, l2) => { setMobileNumberErrorTextLine1(l1); setMobileNumberErrorTextLine2(l2); } },
        // dateOfBirth: { el: dateOfBirthEl, getValue: () => dateOfBirthEl?.current?.value && dateOfBirthEl?.current?.value.trim(), setError: setDateOfBirthErrorText },
    }

    function getData({ validateItem = null, validateItems = [] } = {}) {
        const data = Object.fromEntries(
            Object.keys(dataElements).map(k => {
                return [k, dataElements[k].getValue()]
            })
        );
        data.token = linkToken;
        let numErrors = 0;

        if (validateItem === 'membershipCardID' || validateItem === 'all' || validateItems.includes('membershipCardID') ) {
            if(!['3', '7', '8', '9'].includes(data.membershipCardID.trim()[0]) || data.membershipCardID.trim().length < 8 || data.membershipCardID.trim().length > 12 || (data.membershipCardID !== data.membershipCardID.replace(/[^\d]/g, ''))) {
                dataElements.membershipCardID.setError('Please enter a valid Membership Card ID.', '(e.g.: 82000001)')
                numErrors++;
            } else {
                dataElements.membershipCardID.setError(null, null);
            }
        }

        if (validateItem === 'email' || validateItem === 'all' || validateItems.includes('email')) {
            // eslint-disable-next-line no-control-regex
            const re = new RegExp(/^(?:[a-z0-9+!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i);
            if(!re.test(data.email)) {
                dataElements.email.setError('Please enter a valid email address.', '(e.g.: bob@gmail.com)')
                numErrors++;
            } else {
                dataElements.email.setError(null);
            }
        } else {
            data.email = '_________@x.x.x'
        }

        if (validateItem === 'mobileNumber' || validateItem === 'all' || validateItems.includes('mobileNumber')) {
            // const re = new RegExp(/^(\+[0-9]+\s{0,1}|)[0-9]{8,}$/);
            const re = new RegExp(/^[0-9]{8}$/);
            if(!re.test(data.mobileNumber)) {
                dataElements.mobileNumber.setError('Please enter a valid mobile number.', '(e.g.: 91234567)')
                numErrors++;
            } else {
                dataElements.mobileNumber.setError(null, null);
            }
        } else {
            data.mobileNumber = '00000000'
        }

        if (validateItem === 'dateOfBirth' || validateItem === 'all' || validateItems.includes('dateOfBirth')) {
            if(!data.dateOfBirth || !Number.isFinite((new Date(data.dateOfBirth)).getTime()) || !((new Date(data.dateOfBirth)).getTime() >= -2209013725000) || !((new Date(data.dateOfBirth)) < new Date())) {
                dataElements.dateOfBirth.setError('Please enter a valid date of birth.')
                numErrors++;
            } else {
                dataElements.dateOfBirth.setError(null);
                data.dateOfBirth = (new Date(data.dateOfBirth)).getTime() - 60 * 60 * 1000 * 8

                // Min Date: -2209013725000 + 1 = -2209013724999
            }
        } else {
            data.dateOfBirth = -2209013724999
        }

        return { data, numErrors };
    }

    function attemptRecoverMembershipCardID() {
        if (isRecoveringMembershipID) {
            return;
        }

        const { data, numErrors } = getData({ validateItem: 'email' });
        if (numErrors === 0) {
            setIsRecoveringMembershipID(true);
            setIsDoneRecoveringMembershipID(false);
            const payload = { email: data.email };
            console.log('Submitting:', payload)
            tryRecoverMembershipCardID(payload)
                .then(({ data }) => {
                    console.log(data);
                    setIsRecoveringMembershipID(false);
                    setIsDoneRecoveringMembershipID(true);
                    setTimeout(() => {
                        setAccordionActiveKey('verify-membership')
                    }, 3000)
                })
                .catch(e => {
                    setIsRecoveringMembershipID(false);
                })
        }
    }

    function attemptLink() {
        const { data, numErrors } = getData({ validateItems: [
            'membershipCardID',
            validateWithEmail ? 'email' : '____',
            validateWithMobile ? 'mobileNumber' : '____',
        ] });
        if (numErrors === 0) {
            setOverallErrorText(null)
            console.log('Submitting:', data)
            setIsLinkingUp(true);
            tryLinkMember(data)
                .then(({ data: { success, message, app_token: appToken } }) => {
                    if (success) {
                        navigate(`/telegram-app?token=${appToken}`)
                        setIsDoneLinkingUp(true);
                    } else {
                        setIsLinkingUp(false);
                        setOverallErrorText(message);
                    }
                })
                .catch(e => {
                    setIsLinkingUp(false);
                    setOverallErrorText(`Unable to verify membership. Try again later.`);
                })
        }
    }

    if (!linkToken) {
        return (
            <FullWidthSpinner
                text='Loading...'
                heightInVH={isMobile ? 74 : 100}
                scale={3} />
        );
    }

    if (isDoneLinkingUp) {
        return (
            <FullWidthSpinner
                text='Sign Up Complete. Redirecting...'
                heightInVH={isMobile ? 64 : 90}
                scale={3} />
        );
    }

    return (
        <div>
            <HorizontallyCenteringContainer>
                <div style={{
                    position:'absolute', top: '0px', zIndex: '-1',
                    width: '100%',
                    height: 'calc(20px)',
                    // height: 'calc(20px + 36px + 20px)',
                    backgroundColor: 'var(--app-top-strip-color)'
                    // backgroundColor: 'var(--swensens-brand-color)'
                }}/>
                <div style={{
                    position:'absolute', top: '0px', zIndex: '-2',
                    width: '100%', height: '100vh',
                    backgroundColor: DEFAULT_BACKGROUND_COLOR,
                }}/>

                {
                    isLinkingUp && (
                        <FullWidthSpinner
                            text='Setting Up...'
                            heightInVH={isMobile ? 64 : 90}
                            scale={3} />
                    )
                }

                <div style={{ width: '360px', paddingLeft: '10px', paddingRight: '10px', display: isLinkingUp ? 'none' : '' }}>
                    <div style={{ width: '340px' }}>
                    <div style={{
                        height: 'calc(20px)',
                        backgroundColor: 'var(--app-top-strip-color)'
                    }}/>
                        <VerticalSeparator height='0.5em'/>
                        <h4 className="bold">Verify your Cool Rewards Membership</h4>
                        <hr/>

                        <p className="app-text-left-align thin-line">You are one step away from enjoying our Cool Rewards membership benefits.</p>
                        <VerticalSeparator/>

                        <Accordion activeKey={accordionActiveKey}>
                            <Accordion.Item eventKey="verify-membership" onClick={() => setAccordionActiveKey('verify-membership')}>
                                <Accordion.Header>Verify your Cool Rewards Membership</Accordion.Header>
                                <Accordion.Body>
                                    <p className="app-text-left-align thin-line">Provide your Membership Card ID with either your Email, or Mobile Number for verification.</p>
                                    <VerticalSeparator/>
                                    {
                                        overallErrorText && (
                                            <>
                                                <HorizontallyCenteringContainer>
                                                    <div className="input-error">{overallErrorText}</div>
                                                </HorizontallyCenteringContainer>
                                                <VerticalSeparator/>
                                            </>
                                        )
                                    }

                                    <VerticalSeparator/>
                                    <div>Membership Card ID (required):</div>
                                    <HorizontallyCenteringContainer>
                                        <input ref={membershipCardIDEl} className="underline-input" onChange={() => getData({ validateItem: 'membershipCardID' })}/>
                                    </HorizontallyCenteringContainer>
                                    { membershipCardIDErrorTextLine1 && <div className="input-error">{membershipCardIDErrorTextLine1}</div> }
                                    { membershipCardIDErrorTextLine2 && <div className="input-error">{membershipCardIDErrorTextLine2}</div> }
                                    <VerticalSeparator />

                                    <VerticalSeparator/>
                                    <div>
                                        <Form>
                                            Validate with: &nbsp;&nbsp;
                                            <Form.Check
                                                inline
                                                label='email'
                                                name='email-or-mobile'
                                                type='radio'
                                                onChange={e => {
                                                    if (e.target.checked) {
                                                        setValidateWithEmail(true)
                                                        setValidateWithMobile(false)
                                                    }
                                                }} />
                                            <Form.Check
                                                inline
                                                label='mobile'
                                                name='email-or-mobile'
                                                type='radio'
                                                onChange={e => {
                                                    if (e.target.checked) {
                                                        setValidateWithEmail(false)
                                                        setValidateWithMobile(true)
                                                    }
                                                }} />
                                        </Form>
                                    </div>

                                    <VerticalSeparator />

                                    <div style={{ display: validateWithEmail ? '' : 'none'}}>
                                        <div>Email (required):</div>
                                        <HorizontallyCenteringContainer>
                                            <input ref={emailEl}
                                                className="underline-input"
                                                onChange={() => {
                                                    if (emailRecoveryEl.current && emailEl.current) {
                                                        emailRecoveryEl.current.value = emailEl.current.value
                                                    }
                                                    getData({ validateItem: 'email' });
                                                }}
                                                onKeyUp={e => {
                                                    if (e.key === 'Enter') {
                                                        attemptLink()
                                                    }
                                                }}
                                            />
                                        </HorizontallyCenteringContainer>
                                        { emailErrorTextLine1 && <div className="input-error">{emailErrorTextLine1}</div> }
                                        { emailErrorTextLine2 && <div className="input-error">{emailErrorTextLine2}</div> }
                                        <VerticalSeparator />
                                    </div>

                                    <div style={{ display: validateWithMobile ? '' : 'none'}}>
                                        <div>Mobile Number (required):</div>
                                        <HorizontallyCenteringContainer>
                                            <input ref={mobileNumberEl}
                                                type="tel" pattern="[0-9]{8}"
                                                className="underline-input"
                                                onChange={() => getData({ validateItem: 'mobileNumber' })}
                                                onKeyUp={e => {
                                                    if (e.key === 'Enter') {
                                                        attemptLink()
                                                    }
                                                }}
                                            />
                                        </HorizontallyCenteringContainer>
                                        { mobileNumberErrorTextLine1 && <div className="input-error">{mobileNumberErrorTextLine1}</div> }
                                        { mobileNumberErrorTextLine2 && <div className="input-error">{mobileNumberErrorTextLine2}</div> }
                                        <VerticalSeparator />
                                    </div>

                                    {/* <div>Date of Birth (required):</div>
                                    <HorizontallyCenteringContainer>
                                        <input ref={dateOfBirthEl} type="date" className="underline-input" onChange={() => getData({ validateItem: 'dateOfBirth' })}/>
                                    </HorizontallyCenteringContainer>
                                    { dateOfBirthErrorText && <div className="input-error">{dateOfBirthErrorText}</div> }
                                    <VerticalSeparator /> */}

                                    {
                                        (validateWithEmail || validateWithMobile) && (
                                            <>
                                                <VerticalSeparator/>
                                                <HorizontallyCenteringContainer>
                                                    <Button
                                                        className="w80 swensens-button"
                                                        onClick={attemptLink}
                                                    >Verify Membership</Button>
                                                </HorizontallyCenteringContainer>
                                                <HorizontallyCenteringContainer>
                                                    { overallErrorText && <div className="input-error">{overallErrorText}</div> }
                                                </HorizontallyCenteringContainer>
                                            </>
                                        )
                                    }
                                    
                                    <VerticalSeparator height='1.5em'/>
                                </Accordion.Body>
                            </Accordion.Item>
                            <Accordion.Item eventKey="recover-card-id" onClick={() => setAccordionActiveKey('recover-card-id')}>
                                <Accordion.Header>
                                    <span style={{ color: 'red' }}>
                                        Forgot your Membership Card ID? We can send it to your registered email.
                                    </span>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <p className="app-text-left-align thin-line">Provide your email address and we will send your Membership Card ID if you are registered at that address.</p>
                                    <VerticalSeparator/>

                                    <VerticalSeparator/>
                                    <div>Email (required):</div>
                                    <HorizontallyCenteringContainer>
                                        <input ref={emailRecoveryEl}
                                            className="underline-input"
                                            onChange={() => {
                                                if (emailRecoveryEl.current && emailEl.current) {
                                                    // trigger change event
                                                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set
                                                    nativeInputValueSetter.call(emailEl.current, emailRecoveryEl.current.value)
                                                    emailEl.current.dispatchEvent(new Event('change', { bubbles: true}))
                                                }
                                            }}
                                            onKeyUp={e => {
                                                if (e.key === 'Enter') {
                                                    attemptRecoverMembershipCardID()
                                                }
                                            }}
                                        />
                                    </HorizontallyCenteringContainer>
                                    { emailErrorTextLine1 && <div className="input-error">{emailErrorTextLine1}</div> }
                                    { emailErrorTextLine2 && <div className="input-error">{emailErrorTextLine2}</div> }

                                    <VerticalSeparator/>
                                    <HorizontallyCenteringContainer>
                                        <Button
                                            className="w80 swensens-button"
                                            onClick={attemptRecoverMembershipCardID}
                                        >{isRecoveringMembershipID ? 'Working...' : 'Recover My Membership Card ID'}</Button>
                                    </HorizontallyCenteringContainer>
                                    {
                                        isDoneRecoveringMembershipID && (
                                            <>
                                                <VerticalSeparator/>
                                                <HorizontallyCenteringContainer>
                                                    <p className="app-text thin-line" style={{ color: 'blue' }}>Check your email. If you are registered at that address, you will receive your Membership Card ID.</p>
                                                </HorizontallyCenteringContainer>
                                            </>
                                        ) 
                                    }
                                
                                    <VerticalSeparator height='1.5em'/>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>


                    </div>
                </div>

            </HorizontallyCenteringContainer>
            <VerticalSeparator height='2em'/>
            <VerticalSeparator height='16em'/>
        </div>
    );
}
